import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";

const BRAND = {
  ink: "#1E1B2E",
  text: "#312B45",
  textSoft: "#6E6786",
  primary: "#4B1E78",
  primaryDeep: "#2D0E4F",
  primarySoft: "#F6F0FE",
  border: "#E8DCF8",
  borderSoft: "#F1EAFE",
  white: "#FFFFFF",
  successBg: "#EAF8F0",
  successText: "#1F7A48",
  warningBg: "#FFF5DB",
  warningText: "#A15C00",
  dangerBg: "#FEECEF",
  dangerText: "#B42318",
  neutralBg: "#F4F2F8",
  neutralText: "#5E5873",
};

const STATUS_STYLES = {
  Draft: { backgroundColor: BRAND.neutralBg, color: BRAND.neutralText },
  Open: { backgroundColor: "#EFE8FC", color: BRAND.primary },
  "Pending Approval": { backgroundColor: BRAND.warningBg, color: BRAND.warningText },
  "Issue Raised": { backgroundColor: "#FDEBD7", color: "#A15500" },
  "Not Planned": { backgroundColor: BRAND.neutralBg, color: BRAND.neutralText },
  Resolved: { backgroundColor: BRAND.successBg, color: BRAND.successText },
  Closed: { backgroundColor: BRAND.successBg, color: BRAND.successText },
  Rejected: { backgroundColor: BRAND.dangerBg, color: BRAND.dangerText },
};

const PRIORITY_STYLES = {
  Critical: { backgroundColor: BRAND.dangerBg, color: BRAND.dangerText },
  High: { backgroundColor: "#FDEBD7", color: "#A15500" },
  Medium: { backgroundColor: "#EFE8FC", color: BRAND.primary },
  Low: { backgroundColor: BRAND.neutralBg, color: BRAND.neutralText },
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingRight: 30,
    paddingBottom: 54,
    paddingLeft: 30,
    backgroundColor: BRAND.white,
    color: BRAND.text,
    fontFamily: "Helvetica",
    fontSize: 9.5,
    lineHeight: 1.45,
  },
  docOrgHeader: {
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.primary,
    paddingBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  docOrgInfo: {
    flexDirection: "column",
    maxWidth: "62%",
    alignItems: "flex-end",
  },
  docLogoContainer: {
    width: 80,
    height: 60,
    justifyContent: "center",
    alignItems: "center",
  },
  docLogo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  docLogoFallback: {
    width: 58,
    height: 58,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.primary,
  },
  docTitle: {
    color: BRAND.primary,
    fontSize: 18,
    fontWeight: "bold",
    marginTop: 4,
  },
  orgName: {
    fontSize: 14,
    fontWeight: "bold",
    color: BRAND.primary,
    marginBottom: 4,
    textAlign: "right",
  },
  orgText: {
    fontSize: 9,
    color: BRAND.textSoft,
    marginBottom: 2,
    textAlign: "right",
  },
  docHeader: {
    marginBottom: 14,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  docMeta: {
    flexDirection: "column",
    alignItems: "flex-end",
  },
  metaText: {
    fontSize: 9.2,
    color: BRAND.textSoft,
    marginBottom: 2,
  },
  detailSection: {
    marginBottom: 14,
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 12,
    backgroundColor: BRAND.white,
    paddingTop: 12,
    paddingRight: 12,
    paddingBottom: 12,
    paddingLeft: 12,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    paddingTop: 3,
    paddingBottom: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: BRAND.borderSoft,
  },
  detailLabel: {
    fontSize: 9.2,
    color: BRAND.textSoft,
    width: "32%",
  },
  detailValue: {
    fontSize: 9.2,
    color: BRAND.text,
    textAlign: "right",
    width: "68%",
  },
  topBand: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 58,
    backgroundColor: BRAND.primaryDeep,
  },
  topGlow: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 180,
    height: 58,
    backgroundColor: BRAND.primary,
    opacity: 0.18,
  },
  header: {
    position: "absolute",
    top: 18,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerBrand: {
    flexDirection: "row",
    alignItems: "center",
  },
  logoShell: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: BRAND.white,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginRight: 10,
  },
  logoImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  logoFallback: {
    width: "100%",
    height: "100%",
    backgroundColor: BRAND.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  logoFallbackText: {
    color: BRAND.white,
    fontSize: 12,
    fontWeight: "bold",
    letterSpacing: 0.8,
  },
  headerEyebrow: {
    color: "#D7C2F8",
    fontSize: 8,
    letterSpacing: 1.6,
    textTransform: "uppercase",
  },
  headerTitle: {
    color: BRAND.white,
    fontSize: 12.5,
    fontWeight: "bold",
  },
  headerMeta: {
    color: "#EADFFD",
    fontSize: 8.5,
    textAlign: "right",
  },
  heroCard: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: BRAND.border,
    backgroundColor: BRAND.primarySoft,
    paddingTop: 18,
    paddingRight: 18,
    paddingBottom: 18,
    paddingLeft: 18,
    marginBottom: 16,
  },
  heroKicker: {
    color: BRAND.primary,
    fontSize: 8.5,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  heroTitle: {
    color: BRAND.ink,
    fontSize: 20,
    fontWeight: "bold",
    lineHeight: 1.2,
    marginBottom: 6,
  },
  heroText: {
    color: BRAND.textSoft,
    fontSize: 10,
    marginBottom: 12,
  },
  heroMetaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  heroMetaChip: {
    borderWidth: 1,
    borderColor: BRAND.border,
    backgroundColor: BRAND.white,
    borderRadius: 999,
    paddingTop: 4,
    paddingRight: 8,
    paddingBottom: 4,
    paddingLeft: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  heroMetaText: {
    color: BRAND.text,
    fontSize: 8.8,
  },
  card: {
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 16,
    backgroundColor: BRAND.white,
    paddingTop: 14,
    paddingRight: 14,
    paddingBottom: 14,
    paddingLeft: 14,
    marginBottom: 14,
  },
  sectionKicker: {
    color: BRAND.primary,
    fontSize: 8,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  sectionTitle: {
    color: BRAND.ink,
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 10,
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginLeft: -6,
    marginRight: -6,
  },
  infoItem: {
    width: "50%",
    paddingLeft: 6,
    paddingRight: 6,
    marginBottom: 10,
  },
  infoLabel: {
    color: BRAND.textSoft,
    fontSize: 8,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 2,
  },
  infoValue: {
    color: BRAND.text,
    fontSize: 9.4,
    lineHeight: 1.35,
  },
  statsRow: {
    flexDirection: "row",
    marginLeft: -5,
    marginRight: -5,
    marginBottom: 14,
  },
  statCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 14,
    backgroundColor: BRAND.white,
    paddingTop: 12,
    paddingRight: 12,
    paddingBottom: 12,
    paddingLeft: 12,
    marginLeft: 5,
    marginRight: 5,
  },
  statLabel: {
    color: BRAND.textSoft,
    fontSize: 8.2,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 6,
  },
  statValue: {
    color: BRAND.ink,
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 4,
  },
  statSubtext: {
    color: BRAND.textSoft,
    fontSize: 8.5,
  },
  registerTable: {
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 14,
    overflow: "hidden",
  },
  registerHeader: {
    flexDirection: "row",
    backgroundColor: BRAND.primarySoft,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
    paddingTop: 8,
    paddingBottom: 8,
  },
  registerHeaderText: {
    color: BRAND.primary,
    fontSize: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  registerRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BRAND.borderSoft,
    paddingTop: 8,
    paddingBottom: 8,
  },
  registerRowLast: {
    borderBottomWidth: 0,
  },
  colCode: { width: "13%", paddingLeft: 8, paddingRight: 8 },
  colTitle: { width: "29%", paddingLeft: 8, paddingRight: 8 },
  colProject: { width: "22%", paddingLeft: 8, paddingRight: 8 },
  colStatus: { width: "14%", paddingLeft: 8, paddingRight: 8 },
  colPriority: { width: "10%", paddingLeft: 8, paddingRight: 8 },
  colAssigned: { width: "12%", paddingLeft: 8, paddingRight: 8 },
  tableText: {
    color: BRAND.text,
    fontSize: 8.5,
    lineHeight: 1.35,
  },
  tableMuted: {
    color: BRAND.textSoft,
    fontSize: 7.8,
    marginTop: 2,
  },
  snagSection: {
    paddingTop: 2,
    paddingBottom: 4,
  },
  snagHeaderWrap: {
    marginBottom: 12,
  },
  snagSectionLabel: {
    color: BRAND.primary,
    fontSize: 8.2,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    marginBottom: 5,
  },
  snagTitle: {
    color: BRAND.ink,
    fontSize: 17,
    fontWeight: "bold",
    marginBottom: 8,
    lineHeight: 1.2,
  },
  pillRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 6,
  },
  pill: {
    borderRadius: 999,
    paddingTop: 4,
    paddingRight: 8,
    paddingBottom: 4,
    paddingLeft: 8,
    fontSize: 8.2,
    fontWeight: "bold",
    marginRight: 6,
    marginBottom: 6,
  },
  bodyText: {
    color: BRAND.text,
    fontSize: 9.6,
    lineHeight: 1.55,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 6,
  },
  chip: {
    borderWidth: 1,
    borderColor: BRAND.border,
    backgroundColor: BRAND.primarySoft,
    color: BRAND.primary,
    borderRadius: 999,
    paddingTop: 3,
    paddingRight: 7,
    paddingBottom: 3,
    paddingLeft: 7,
    fontSize: 8.4,
    marginRight: 6,
    marginBottom: 6,
  },
  imageGrid: {
    flexDirection: "row",
    marginLeft: -6,
    marginRight: -6,
    marginBottom: 2,
  },
  imageCard: {
    width: "50%",
    paddingLeft: 6,
    paddingRight: 6,
  },
  imageShell: {
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: "#FBFAFD",
  },
  imageHeader: {
    paddingTop: 9,
    paddingRight: 10,
    paddingBottom: 9,
    paddingLeft: 10,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.borderSoft,
    backgroundColor: BRAND.white,
  },
  imageHeaderTitle: {
    color: BRAND.ink,
    fontSize: 9,
    fontWeight: "bold",
  },
  imageStage: {
    height: 186,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F7F4FB",
  },
  issueImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  emptyImageText: {
    color: BRAND.textSoft,
    fontSize: 9,
  },
  timelineCard: {
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 16,
    backgroundColor: BRAND.white,
    paddingTop: 14,
    paddingRight: 14,
    paddingBottom: 14,
    paddingLeft: 14,
    marginBottom: 12,
  },
  timelineItem: {
    paddingBottom: 12,
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.borderSoft,
  },
  timelineItemLast: {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
  timelineTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  timelineUser: {
    color: BRAND.ink,
    fontSize: 9.4,
    fontWeight: "bold",
    marginBottom: 2,
  },
  timelineMeta: {
    color: BRAND.textSoft,
    fontSize: 8.2,
  },
  timelineText: {
    color: BRAND.text,
    fontSize: 9.2,
    lineHeight: 1.45,
    marginTop: 4,
  },
  timelineAttachment: {
    marginTop: 8,
    width: 160,
    height: 104,
    borderRadius: 10,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: BRAND.border,
    backgroundColor: "#FBFAFD",
  },
  timelineAttachmentImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  timelineTable: {
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 12,
    overflow: "hidden",
  },
  timelineHeaderRow: {
    flexDirection: "row",
    backgroundColor: BRAND.primarySoft,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
    paddingTop: 7,
    paddingBottom: 7,
  },
  timelineDataRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BRAND.borderSoft,
    paddingTop: 7,
    paddingBottom: 7,
  },
  timelineDataRowLast: {
    borderBottomWidth: 0,
  },
  timelineColWho: {
    width: "20%",
    paddingLeft: 8,
    paddingRight: 8,
  },
  timelineColEvent: {
    width: "16%",
    paddingLeft: 8,
    paddingRight: 8,
  },
  timelineColTime: {
    width: "20%",
    paddingLeft: 8,
    paddingRight: 8,
  },
  timelineColComment: {
    width: "34%",
    paddingLeft: 8,
    paddingRight: 8,
  },
  timelineColAttachment: {
    width: "10%",
    paddingLeft: 8,
    paddingRight: 8,
  },
  timelineHeaderText: {
    color: BRAND.primary,
    fontSize: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.7,
  },
  timelineCellText: {
    color: BRAND.text,
    fontSize: 8.4,
    lineHeight: 1.35,
  },
  emptyState: {
    color: BRAND.textSoft,
    fontSize: 9.2,
    fontStyle: "italic",
  },
  footerRule: {
    position: "absolute",
    left: 30,
    right: 30,
    bottom: 34,
    height: 1,
    backgroundColor: BRAND.border,
  },
  footer: {
    position: "absolute",
    left: 30,
    right: 30,
    bottom: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerText: {
    color: BRAND.textSoft,
    fontSize: 8,
  },
});

const toDisplayString = (value, fallback = "N/A") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
};

const buildAddress = (...parts) =>
  parts
    .flat()
    .filter((part) => part !== null && part !== undefined && String(part).trim())
    .map((part) => String(part).trim())
    .join(", ");

const formatDate = (value, { includeTime = false } = {}) => {
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return toDisplayString(value);

  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "numeric",
          minute: "2-digit",
        }
      : {}),
  });
};

const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "SM";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const truncateText = (value, maxLength = 70) => {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}...`;
};

const getUniqueProjectsCount = (snags = []) =>
  new Set(snags.map((snag) => toDisplayString(snag?.project, "")).filter(Boolean))
    .size;

const getStatusCount = (snags = [], statuses = []) =>
  snags.filter((snag) => statuses.includes(String(snag?.status || ""))).length;

const getPriorityCount = (snags = [], priorities = []) =>
  snags.filter((snag) => priorities.includes(String(snag?.priority || ""))).length;

const getUniqueValues = (snags = [], key) =>
  Array.from(
    new Set(
      snags
        .map((snag) => toDisplayString(snag?.[key], ""))
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

const getPillStyle = (toneMap, key) => toneMap[key] || toneMap.default || {};

function SummaryStat({ label, value, subtext }) {
  return (
    <View style={styles.statCard}>
      <Text style={styles.statLabel}>{label}</Text>
      <Text style={styles.statValue}>{value}</Text>
      {subtext ? <Text style={styles.statSubtext}>{subtext}</Text> : null}
    </View>
  );
}

function Pill({ label, stylesMap }) {
  return (
    <Text style={[styles.pill, getPillStyle(stylesMap, label)]}>
      {toDisplayString(label)}
    </Text>
  );
}

function InfoItem({ label, value }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{toDisplayString(value)}</Text>
    </View>
  );
}

function DocumentOrgHeader({ organization, title }) {
  const orgName = toDisplayString(organization?.organization_name, "Organization");
  const logoSource =
    organization?.logoSource || organization?.logo_image_file || organization?.logo_image || "";

  return (
    <View style={styles.docOrgHeader}>
      <View style={{ flexDirection: "column", gap: 6 }}>
        <View style={styles.docLogoContainer}>
          {logoSource ? (
            <Image src={logoSource} style={styles.docLogo} />
          ) : (
            <View style={styles.docLogoFallback}>
              <Text style={styles.logoFallbackText}>{getInitials(orgName)}</Text>
            </View>
          )}
        </View>
        {title ? <Text style={styles.docTitle}>{title}</Text> : null}
      </View>

      <View style={styles.docOrgInfo}>
        <Text style={styles.orgName}>{orgName}</Text>
        <Text style={styles.orgText}>{toDisplayString(organization?.business_address, "")}</Text>
        <Text style={styles.orgText}>
          {organization?.city ? `${organization.city} ` : ""}
          {organization?.state ? `${organization.state}` : ""}
          {organization?.pincode ? ` - ${organization.pincode}` : ""}
        </Text>
        {organization?.contact_number ? (
          <Text style={styles.orgText}>Ph: {organization.contact_number}</Text>
        ) : null}
        {organization?.user_email ? (
          <Text style={styles.orgText}>{organization.user_email}</Text>
        ) : null}
        {organization?.website ? <Text style={styles.orgText}>{organization.website}</Text> : null}
      </View>
    </View>
  );
}

function ImageCard({ title, src, emptyText }) {
  return (
    <View style={styles.imageCard} wrap={false}>
      <View style={styles.imageShell}>
        <View style={styles.imageHeader}>
          <Text style={styles.imageHeaderTitle}>{title}</Text>
        </View>
        <View style={styles.imageStage}>
          {src ? (
            <Image src={src} style={styles.issueImage} />
          ) : (
            <Text style={styles.emptyImageText}>{emptyText}</Text>
          )}
        </View>
      </View>
    </View>
  );
}

function TimelineTableRow({ activity, isLast }) {
  return (
    <View style={[styles.timelineDataRow, isLast && styles.timelineDataRowLast]} wrap={false}>
      <View style={styles.timelineColWho}>
        <Text style={styles.timelineCellText}>{toDisplayString(activity?.user, "User")}</Text>
      </View>
      <View style={styles.timelineColEvent}>
        <Text style={styles.timelineCellText}>
          {toDisplayString(activity?.typeLabel || activity?.type, "Update")}
        </Text>
      </View>
      <View style={styles.timelineColTime}>
        <Text style={styles.timelineCellText}>
          {formatDate(activity?.timestamp, { includeTime: true })}
        </Text>
      </View>
      <View style={styles.timelineColComment}>
        <Text style={styles.timelineCellText}>
          {toDisplayString(activity?.text, "Activity updated")}
        </Text>
      </View>
      <View style={styles.timelineColAttachment}>
        <Text style={styles.timelineCellText}>
          {activity?.attachmentSource || activity?.attachment ? "Yes" : "-"}
        </Text>
      </View>
    </View>
  );
}

export default function SnagsPDFDocument({
  organization = {},
  snags = [],
  generatedAt,
}) {
  const orgName = toDisplayString(organization?.organization_name, "Organization");
  const totalSnags = snags.length;
  const openCount = getStatusCount(snags, ["Open", "Pending Approval", "Issue Raised"]);
  const resolvedCount = getStatusCount(snags, ["Resolved", "Closed"]);
  const criticalCount = getPriorityCount(snags, ["Critical"]);
  const notPlannedCount = getStatusCount(snags, ["Not Planned"]);
  const projectsCount = getUniqueProjectsCount(snags);
  const uniqueProjects = getUniqueValues(snags, "project");
  const uniqueSites = getUniqueValues(snags, "location");
  const primaryProject = toDisplayString(uniqueProjects[0], "N/A");
  const primarySite = toDisplayString(uniqueSites[0], "N/A");
  const projectsCovered = uniqueProjects.length ? uniqueProjects.join(", ") : "N/A";
  const siteLocations = uniqueSites.length ? uniqueSites.join(", ") : "N/A";
  const organizationAddress = buildAddress(
    organization?.business_address,
    organization?.city,
    organization?.state,
    organization?.pincode,
  );

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <DocumentOrgHeader organization={organization} title="Snags Report" />

        <View style={styles.docHeader}>
          <View>
            <Text style={styles.metaText}>Organization: {orgName}</Text>
            <Text style={styles.metaText}>
              Total Snags: {totalSnags} | Projects: {projectsCount}
            </Text>
          </View>
          <View style={styles.docMeta}>
            <Text style={styles.metaText}>
              Date: {formatDate(generatedAt, { includeTime: true })}
            </Text>
          </View>
        </View>
        {/* <View style={styles.detailSection}>
          <Text style={styles.sectionTitle}>Project & Site Details</Text>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Project</Text>
            <Text style={styles.detailValue}>{primaryProject}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Projects Covered</Text>
            <Text style={styles.detailValue}>{truncateText(projectsCovered, 110)}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Site</Text>
            <Text style={styles.detailValue}>{primarySite}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Site Locations</Text>
            <Text style={styles.detailValue}>{truncateText(siteLocations, 110)}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Site Address</Text>
            <Text style={styles.detailValue}>{toDisplayString(organizationAddress)}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Contact</Text>
            <Text style={styles.detailValue}>
              {toDisplayString(organization?.contact_number, "N/A")}
            </Text>
          </View>
          <View style={[styles.detailRow, { borderBottomWidth: 0, marginBottom: 0 }]}>
            <Text style={styles.detailLabel}>Email</Text>
            <Text style={styles.detailValue}>
              {toDisplayString(organization?.user_email, "N/A")}
            </Text>
          </View>
        </View> */}
        <View style={styles.statsRow}>
          <SummaryStat
            label="Total Snags"
            value={String(totalSnags)}
            subtext="Full organization register"
          />
          <SummaryStat
            label="Active Queue"
            value={String(openCount)}
            subtext="Open, pending approval, issue raised"
          />
          <SummaryStat
            label="Resolved"
            value={String(resolvedCount)}
            subtext={`${notPlannedCount} marked not planned`}
          />
          <SummaryStat
            label="Critical"
            value={String(criticalCount)}
            subtext="Highest priority issues"
          />
        </View>

        <View style={styles.card} wrap={false}>
          <Text style={styles.sectionKicker}>Latest First Register</Text>
          <Text style={styles.sectionTitle}>Snag Register</Text>
          <View style={styles.registerTable}>
            <View style={styles.registerHeader}>
              <View style={styles.colCode}>
                <Text style={styles.registerHeaderText}>Code</Text>
              </View>
              <View style={styles.colTitle}>
                <Text style={styles.registerHeaderText}>Snag</Text>
              </View>
              <View style={styles.colProject}>
                <Text style={styles.registerHeaderText}>Project</Text>
              </View>
              <View style={styles.colStatus}>
                <Text style={styles.registerHeaderText}>Status</Text>
              </View>
              <View style={styles.colPriority}>
                <Text style={styles.registerHeaderText}>Priority</Text>
              </View>
              <View style={styles.colAssigned}>
                <Text style={styles.registerHeaderText}>Assignees</Text>
              </View>
            </View>
            {snags.map((snag, index) => (
              <View
                key={`register-${snag.id || index}`}
                style={[styles.registerRow, index === snags.length - 1 && styles.registerRowLast]}
              >
                <View style={styles.colCode}>
                  <Text style={styles.tableText}>{toDisplayString(snag?.code)}</Text>
                </View>
                <View style={styles.colTitle}>
                  <Text style={styles.tableText}>{truncateText(snag?.title, 58)}</Text>
                  <Text style={styles.tableMuted}>
                    {formatDate(snag?.createdAt)} • {toDisplayString(snag?.location)}
                  </Text>
                </View>
                <View style={styles.colProject}>
                  <Text style={styles.tableText}>{truncateText(snag?.project, 34)}</Text>
                </View>
                <View style={styles.colStatus}>
                  <Text style={styles.tableText}>{toDisplayString(snag?.status)}</Text>
                </View>
                <View style={styles.colPriority}>
                  <Text style={styles.tableText}>{toDisplayString(snag?.priority)}</Text>
                </View>
                <View style={styles.colAssigned}>
                  <Text style={styles.tableText}>
                    {snag?.assignedTo?.length
                      ? truncateText(snag.assignedTo.join(", "), 32)
                      : "Unassigned"}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {snags.map((snag, index) => (
          <View key={`snag-${snag.id || index}`} style={styles.snagSection} break>
            <View style={styles.snagHeaderWrap}>
              <Text style={styles.snagSectionLabel}>
                Detailed Snag {String(index + 1).padStart(2, "0")}
              </Text>
              <Text style={styles.snagTitle}>
                {toDisplayString(snag?.code)} - {toDisplayString(snag?.title)}
              </Text>
              <View style={styles.pillRow}>
                <Pill label={snag?.status} stylesMap={STATUS_STYLES} />
                <Pill label={snag?.priority} stylesMap={PRIORITY_STYLES} />
              </View>
            </View>

            <View style={styles.card}>
              <Text style={styles.sectionKicker}>Issue Overview</Text>
              <Text style={styles.sectionTitle}>Snag Details</Text>
              <View style={styles.infoGrid}>
                <InfoItem label="Project" value={snag?.project} />
                <InfoItem label="Location" value={snag?.location} />
                <InfoItem label="Category" value={`${toDisplayString(snag?.category)}${snag?.subcategory ? ` - ${snag.subcategory}` : ""}`} />
                <InfoItem label="Created By" value={snag?.createdBy} />
                <InfoItem label="Created On" value={formatDate(snag?.createdAt)} />
                <InfoItem label="Expected Closure" value={formatDate(snag?.expectedClosure)} />
              </View>
              <Text style={styles.infoLabel}>Description</Text>
              <Text style={styles.bodyText}>
                {toDisplayString(snag?.description, "No description was added for this snag.")}
              </Text>

              <Text style={[styles.infoLabel, { marginTop: 12 }]}>Assignees</Text>
              {Array.isArray(snag?.assignedTo) && snag.assignedTo.length > 0 ? (
                <View style={styles.chipsWrap}>
                  {snag.assignedTo.map((name) => (
                    <Text key={`${snag.id}-${name}`} style={styles.chip}>
                      {name}
                    </Text>
                  ))}
                </View>
              ) : (
                <Text style={[styles.bodyText, { marginTop: 2 }]}>No assignees mapped.</Text>
              )}
            </View>

            <View style={styles.card} wrap={false}>
              <Text style={styles.sectionKicker}>Issue Imagery</Text>
              <Text style={styles.sectionTitle}>Original vs Resolved</Text>
              <View style={styles.imageGrid}>
                <ImageCard
                  title="Original Issue"
                  src={snag?.imageSource || snag?.image}
                  emptyText="No original issue image available"
                />
                <ImageCard
                  title="Resolved Issue"
                  src={snag?.resolvedImageSource || snag?.resolvedImage}
                  emptyText="No resolved issue image uploaded"
                />
              </View>
            </View>

            <View style={styles.timelineCard} wrap={false}>
              <Text style={styles.sectionKicker}>Activity Timeline</Text>
              <Text style={styles.sectionTitle}>Timeline & Actions</Text>
              {Array.isArray(snag?.activities) && snag.activities.length > 0 ? (
                <View style={styles.timelineTable}>
                  <View style={styles.timelineHeaderRow}>
                    <View style={styles.timelineColWho}>
                      <Text style={styles.timelineHeaderText}>Who Created</Text>
                    </View>
                    <View style={styles.timelineColEvent}>
                      <Text style={styles.timelineHeaderText}>Event</Text>
                    </View>
                    <View style={styles.timelineColTime}>
                      <Text style={styles.timelineHeaderText}>Time</Text>
                    </View>
                    <View style={styles.timelineColComment}>
                      <Text style={styles.timelineHeaderText}>Comment</Text>
                    </View>
                    <View style={styles.timelineColAttachment}>
                      <Text style={styles.timelineHeaderText}>Att.</Text>
                    </View>
                  </View>
                  {snag.activities.map((activity, activityIndex) => (
                    <TimelineTableRow
                      key={`${snag.id}-activity-${activity.id || activityIndex}`}
                      activity={activity}
                      isLast={activityIndex === snag.activities.length - 1}
                    />
                  ))}
                </View>
              ) : (
                <Text style={styles.emptyState}>No activity available for this snag.</Text>
              )}
            </View>

          </View>
        ))}

        <View fixed style={styles.footerRule} />
        <View fixed style={styles.footer}>
          <Text style={styles.footerText}>SiteMaster organization report</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
}
